/**
 * What the page actually looks like, measured from RENDERED geometry.
 *
 * The other rule sets read the Design JSON, which carries no geometry — the
 * model never writes x/y/width/height, the renderer resolves them. That is why
 * a flyer could score 100/100 while the browser showed a headline smaller than
 * its own footer and a third of the canvas empty: nothing the critic read could
 * see either fact.
 *
 * These signals come from the rendered document, so the critic judges the same
 * thing an operator judges. Pure and deterministic: geometry in, numbers out.
 */

const asArray = (v) => (Array.isArray(v) ? v : []);
const asObject = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v : {});
const num = (v, fallback = 0) => (Number.isFinite(v) ? v : fallback);

/**
 * Words that mark a text node as metadata, wherever they appear in its role or
 * its id. Substring matching is deliberate: models name things `footerLegal`,
 * `phoneBlock`, `contactRow` — an exact-match set saw none of them, and the
 * inverted-hierarchy rule went quiet on exactly the documents it existed for.
 */
export const METADATA_WORDS = Object.freeze([
  'footer', 'legal', 'contact', 'caption', 'disclaimer', 'meta',
  'phone', 'email', 'address', 'copyright', 'social', 'website', 'url',
]);

/** @deprecated kept as the exact-role view of METADATA_WORDS. */
export const METADATA_ROLES = new Set(METADATA_WORDS);

/** Roles that state a node is part of the message, whatever it is called. */
const FOREGROUND_ROLES = new Set(['headline', 'primary-headline', 'title', 'hero-headline', 'display', 'subheadline', 'cta', 'offer', 'eyebrow', 'kicker']);

const looksLikeMetadata = (el) => {
  const meta = asObject(el && el.designV2);
  const role = `${meta.role || ''}`.toLowerCase().trim();

  // A declared ROLE is a decision; an id is only a name. When the model has
  // said what a node is, that wins over a substring in its id — a call to
  // action headed "Contact Us Today!" is the ask, not the small print, and
  // counting it as metadata reported an inverted hierarchy on a design whose
  // hierarchy was right.
  if (role) {
    if (FOREGROUND_ROLES.has(role)) return false;
    return METADATA_WORDS.some((w) => role.includes(w));
  }

  const hay = `${el && el.id ? el.id : ''}`.toLowerCase();
  return METADATA_WORDS.some((w) => hay.includes(w));
};

/** Roles a primary headline may carry. */
export const HEADLINE_ROLES = new Set(['headline', 'title', 'hero', 'display']);

/** Type tokens that read as display scale. */
export const DISPLAY_STYLES = new Set(['display', 'h1']);

const metaOf = (el) => asObject(el && el.designV2);

/** How much of the canvas width an element must span to read as a backdrop band. */
const FULL_BLEED_SHARE = 0.98;

/**
 * A page background is not content.
 *
 * The renderer turns every background fill beyond the first into a full-bleed
 * band carrying `layer: 'background'`. Counting those as coverage made the page
 * look 100% occupied whatever else was on it, and made the biggest "focal mass"
 * always the background — so the two rules that exist to catch an empty,
 * unanchored composition could never fire on any real design, every one of
 * which has a background.
 */
const isBackdrop = (el, canvas) => {
  const meta = metaOf(el);
  if (meta.role === 'background-layer') return true;
  if (meta.layer !== 'background') return false;

  // `layer` is a Z-ORDER BAND, not a statement that something is decorative.
  // A hero photograph is placed on the background layer too, and excluding the
  // whole band cost us exactly the element these rules exist to find: a real
  // flyer's 9.9% hero photo was dropped here, the 7.8% CTA button became the
  // "largest mass" by default, and no-focal-mass fired on a design that had a
  // hero. Only something actually spanning the canvas is a backdrop.
  if (!canvas || !num(canvas.width)) return true; // can't measure: keep the old, safe reading
  return num(el.width) >= num(canvas.width) * FULL_BLEED_SHARE
    && num(el.x) <= num(canvas.width) * (1 - FULL_BLEED_SHARE);
};

/** Elements that actually carry the composition. */
export const contentElements = (elements, canvas) =>
  asArray(elements).filter((el) => !isBackdrop(el, canvas));

/**
 * How much a text element commands the page.
 *
 * Not font size alone: a 60px line in a corner does not lead a composition the
 * way a 44px line across the top does. Size sets the base, weight and width
 * modulate it, and vertical position matters because the eye starts at the top.
 */
export const prominence = (el, canvas) => {
  const size = num(el.fontSize, 0);
  if (!size) return 0;
  const weight = num(parseInt(el.fontWeight, 10), 400);
  const widthShare = canvas.width ? num(el.width) / canvas.width : 0;
  const verticalRank = canvas.height ? 1 - Math.min(1, num(el.y) / canvas.height) : 0.5;

  // Weight above regular counts for something, but never as much as scale.
  const weightFactor = 1 + Math.max(0, weight - 400) / 1000;
  return size * weightFactor * (0.6 + 0.4 * widthShare) * (0.75 + 0.25 * verticalRank);
};

/** Every text element with its measured prominence, strongest first. */
export const textRanking = (elements, canvas) => elements
  .filter((el) => el.type === 'text' && String(el.text || '').trim())
  .map((el) => ({
    id: el.id,
    text: String(el.text || '').slice(0, 60),
    role: metaOf(el).role || null,
    fontSize: num(el.fontSize),
    fontWeight: el.fontWeight || null,
    prominence: Math.round(prominence(el, canvas) * 100) / 100,
    isMetadata: looksLikeMetadata(el),
    isDisplayStyle: DISPLAY_STYLES.has(metaOf(el).typeStyle) || num(el.fontSize) >= canvas.width * 0.045,
  }))
  .sort((a, b) => b.prominence - a.prominence);

/**
 * How much of the canvas carries something, and where the emptiness sits.
 *
 * Whitespace is measured in horizontal bands rather than as one ratio, because
 * a design with generous margins all round reads as composed while the same
 * amount of emptiness pooled in the bottom third reads as unfinished.
 */
export const occupancy = (elements, canvas, bands = 6) => {
  const W = num(canvas.width, 1);
  const H = num(canvas.height, 1);
  const painted = contentElements(elements, canvas)
    .filter((el) => el.type !== 'group' && num(el.width) > 0 && num(el.height) > 0);

  const bandHeight = H / bands;
  const bandCoverage = Array.from({ length: bands }, (_, i) => {
    const top = i * bandHeight;
    const bottom = top + bandHeight;
    // Union of horizontal extents that intersect this band, measured in columns
    // so two overlapping elements are not counted twice.
    const columns = new Array(40).fill(false);
    painted.forEach((el) => {
      const y0 = num(el.y);
      const y1 = y0 + num(el.height);
      if (y1 <= top || y0 >= bottom) return;
      const c0 = Math.max(0, Math.floor((num(el.x) / W) * 40));
      const c1 = Math.min(39, Math.ceil(((num(el.x) + num(el.width)) / W) * 40) - 1);
      for (let c = c0; c <= c1; c += 1) columns[c] = true;
    });
    return columns.filter(Boolean).length / 40;
  });

  // The longest run of near-empty bands — an accidental hole reads as a run.
  const EMPTY = 0.08;
  let longestRun = 0;
  let run = 0;
  let runStart = -1;
  let holeStart = -1;
  bandCoverage.forEach((c, i) => {
    if (c <= EMPTY) {
      if (run === 0) runStart = i;
      run += 1;
      if (run > longestRun) { longestRun = run; holeStart = runStart; }
    } else run = 0;
  });

  return {
    bands: bandCoverage.map((c) => Math.round(c * 100) / 100),
    meanCoverage: Math.round((bandCoverage.reduce((a, b) => a + b, 0) / bands) * 100) / 100,
    emptyBandRun: longestRun,
    emptyBandRatio: Math.round((longestRun / bands) * 100) / 100,
    emptyRunAtEdge: holeStart === 0 || holeStart + longestRun === bands,
    holeStartBand: holeStart,
  };
};

/** The largest painted mass — the thing a viewer sees first. */
export const focalMass = (elements, canvas) => {
  const area = (el) => num(el.width) * num(el.height);
  const canvasArea = num(canvas.width, 1) * num(canvas.height, 1);
  const candidates = contentElements(elements, canvas).filter((el) => ['image', 'frame', 'shape'].includes(el.type));
  const biggest = candidates.reduce((best, el) => (area(el) > area(best || {}) ? el : best), null);
  return biggest
    ? { id: biggest.id, type: biggest.type, share: Math.round((area(biggest) / canvasArea) * 100) / 100 }
    : null;
};

/**
 * Every signal for one rendered page.
 *
 * @param {Object} rendered  a document from the Rendering Engine.
 * @returns {Object|null} null when there is no geometry to read.
 */

/** Roles that mark a node as the piece's semantic primary headline. */
export const PRIMARY_HEADLINE_ROLES = new Set(['headline', 'primary-headline', 'title', 'hero-headline', 'display']);

/** The share of the canvas one element's box covers. */
const shareOf = (el, canvas) => {
  const area = num(canvas.width, 1) * num(canvas.height, 1);
  return area ? Math.round((num(el.width) * num(el.height)) / area * 100) / 100 : 0;
};

/**
 * The photograph the DESIGNER nominated as the hero, and how big it actually
 * rendered.
 *
 * `focalMass` answers "is anything anchoring this page", which a large CTA
 * button can satisfy. This answers a different question: when the design says
 * it leads with a photograph, does that photograph actually lead? A hero
 * declared at 4% of the canvas is a thumbnail with ambitions.
 */
export const heroImageSignal = (elements, canvas) => {
  const hero = contentElements(elements, canvas).find((el) => {
    const meta = metaOf(el);
    const role = `${meta.sectionRole || meta.role || ''}`.toLowerCase();
    return ['image', 'frame'].includes(el.type) && (role === 'hero' || PRIMARY_HEADLINE_ROLES.has(role));
  });
  if (!hero) return null;
  return {
    id: hero.id,
    share: shareOf(hero, canvas),
    resolved: Boolean(metaOf(hero).resolvedAsset),
  };
};

/**
 * The call to action's visual weight.
 *
 * A CTA must be findable and must NOT be the loudest thing on the page. When a
 * button outweighs everything else, the eye lands on "Book Now" before it has
 * been told what is being offered.
 */
export const ctaSignal = (elements, canvas) => {
  const cta = contentElements(elements, canvas).filter((el) => {
    const meta = metaOf(el);
    return `${meta.sectionRole || meta.role || ''}`.toLowerCase() === 'cta';
  });
  if (!cta.length) return null;
  const biggest = cta.reduce((best, el) => (
    num(el.width) * num(el.height) > num(best.width) * num(best.height) ? el : best
  ), cta[0]);
  return { id: biggest.id, share: shareOf(biggest, canvas), count: cta.length };
};

/** Whether any text node is explicitly marked as the primary headline. */
export const semanticHeadline = (elements) => {
  const hit = asArray(elements).find((el) => {
    if (el.type !== 'text') return false;
    const role = `${metaOf(el).role || ''}`.toLowerCase();
    return PRIMARY_HEADLINE_ROLES.has(role);
  });
  return hit ? { id: hit.id, fontSize: num(hit.fontSize) } : null;
};


/**
 * Elements that fall outside the page.
 *
 * A design can validate, render, and read as complete while most of it sits
 * thousands of pixels below the canvas — one malformed line-height did exactly
 * that, and the review still scored it 80 because every other signal is
 * computed from elements it never sees. Anything off the page is measured here
 * so that it cannot pass silently.
 */
export const outOfBounds = (elements, canvas) => {
  const w = num(canvas.width, 0);
  const h = num(canvas.height, 0);
  if (!w || !h) return { count: 0, worstOverflow: 0, ids: [] };

  // A hair of overhang is a rounding artefact, not a defect; a full-bleed band
  // is meant to reach the edge.
  const slack = 2;
  const offenders = contentElements(elements, canvas).filter((el) => (
    num(el.x) < -slack
    || num(el.y) < -slack
    || num(el.x) + num(el.width) > w + slack
    || num(el.y) + num(el.height) > h + slack
  ));

  const worst = offenders.reduce((most, el) => Math.max(
    most,
    (num(el.y) + num(el.height)) - h,
    (num(el.x) + num(el.width)) - w,
    -num(el.x),
    -num(el.y),
  ), 0);

  return {
    count: offenders.length,
    worstOverflow: Math.round(worst),
    ids: offenders.slice(0, 4).map((el) => el.id),
  };
};


/**
 * Text drawn on top of other text.
 *
 * Overlap in general is not a defect — a headline over a photograph, a label on
 * a button plate, a caption in a gradient scrim are all ordinary design. Two
 * TEXT boxes on top of each other are not: it is always a measurement failure,
 * and it is what a mis-measured wrapping headline produces. Keeping the rule to
 * text-on-text is what makes it safe to act on.
 */
export const textCollisions = (elements, canvas) => {
  const texts = contentElements(elements, canvas).filter((el) => el.type === 'text' && num(el.width) > 0 && num(el.height) > 0);

  // A little tolerance: descenders and rounding routinely touch by a pixel or two.
  const slack = 3;
  const pairs = [];
  for (let i = 0; i < texts.length; i += 1) {
    for (let j = i + 1; j < texts.length; j += 1) {
      const a = texts[i];
      const b = texts[j];
      const overlapX = Math.min(num(a.x) + num(a.width), num(b.x) + num(b.width)) - Math.max(num(a.x), num(b.x));
      const overlapY = Math.min(num(a.y) + num(a.height), num(b.y) + num(b.height)) - Math.max(num(a.y), num(b.y));
      if (overlapX <= slack || overlapY <= slack) continue;

      // Only count a real collision, not a graze.
      const smaller = Math.min(num(a.width) * num(a.height), num(b.width) * num(b.height));
      if (!smaller || (overlapX * overlapY) / smaller < 0.12) continue;
      pairs.push([a.id, b.id]);
    }
  }
  return { count: pairs.length, pairs: pairs.slice(0, 3) };
};

export const visualSignals = (rendered) => {
  const page = asArray(asObject(rendered).pages)[0];
  if (!page) return null;
  const elements = asArray(page.elements);
  if (!elements.length) return null;

  const canvas = asObject(page.canvasSize).width
    ? page.canvasSize
    : { width: num(asObject(rendered).canvas_width, 1080), height: num(asObject(rendered).canvas_height, 1350) };

  const ranked = textRanking(elements, canvas);
  const nonMetadata = ranked.filter((t) => !t.isMetadata);
  const metadata = ranked.filter((t) => t.isMetadata);

  const primary = nonMetadata[0] || null;
  const second = nonMetadata[1] || null;
  const topMetadata = metadata[0] || null;

  return {
    canvas,
    elementCount: elements.length,
    ranked,
    primary,
    second,
    topMetadata,
    // How far the lead stands above what follows it. 1 means they are equal.
    dominanceOverNext: primary && second && second.prominence > 0
      ? Math.round((primary.prominence / second.prominence) * 100) / 100
      : null,
    // Below 1 means metadata outranks the headline — an inverted hierarchy.
    dominanceOverMetadata: primary && topMetadata && topMetadata.prominence > 0
      ? Math.round((primary.prominence / topMetadata.prominence) * 100) / 100
      : null,
    typeLevels: new Set(ranked.map((t) => Math.round(t.fontSize))).size,
    largestFontSize: ranked.length ? ranked[0].fontSize : 0,
    displayRatio: canvas.width ? Math.round((ranked.length ? ranked[0].fontSize : 0) / canvas.width * 1000) / 1000 : 0,
    occupancy: occupancy(elements, canvas),
    focal: focalMass(elements, canvas),
    heroImage: heroImageSignal(elements, canvas),
    cta: ctaSignal(elements, canvas),
    semanticHeadline: semanticHeadline(elements),
    outOfBounds: outOfBounds(elements, canvas),
    textCollisions: textCollisions(elements, canvas),
  };
};
